import {
    createSlice,
    createAsyncThunk,
    createEntityAdapter,
} from "@reduxjs/toolkit";
import { productsAPI } from "../../services/productsService";
import { IFilters, IProduct, ICuratedKit, IKitType } from "Interfaces/interface.productsapi";
import { RootState } from "store/Store";

const kitsAdapter = createEntityAdapter({
    selectId: (kit: ICuratedKit) => kit.id || "",
});

interface KitsState {
    status: "idle" | "loading" | "succeeded" | "failed";
    error: string | null;
    totalCount: number;
    currentPage: number;
    hasReachedEnd: boolean;
    selectedKit: ICuratedKit | null;
    isKitLoaded: boolean;
    types: IKitType[];
    typesStatus: "idle" | "loading" | "succeeded" | "failed";
    isTypesLoaded: boolean;
}

const initialState = kitsAdapter.getInitialState<KitsState>({
    status: "idle",
    error: null,
    totalCount: 0,
    currentPage: 1,
    hasReachedEnd: false,
    selectedKit: null,
    isKitLoaded: false,
    types: [],
    typesStatus: "idle",
    isTypesLoaded: false,
});

export const fetchKits = createAsyncThunk(
    "kits/fetchKits",
    async (filters: IFilters, { rejectWithValue }) => {
        try {
            const response = await productsAPI.fetchProductGroups({ ...filters, isActive: true });
            return response;
        } catch (error: any) {
            return rejectWithValue(error.message);
        }
    }
);

export const fetchKitById = createAsyncThunk(
    "kits/fetchKitById",
    async (id: string, { rejectWithValue }) => {
        try {
            const kit = await productsAPI.fetchProductGroupById(id);
            return kit;
        } catch (error: any) {
            return rejectWithValue(error.message);
        }
    }
);

export const fetchKitTypes = createAsyncThunk(
    "kits/fetchKitTypes",
    async (_, { rejectWithValue }) => {
        try {
            const types = await productsAPI.fetchKitTypes();
            const mappedTypes = types.map((type: any, index: number) => ({
                id: type.id || index.toString(),
                name: type.name || "",
                value: type.id || "", 
            }));
            return mappedTypes;
        } catch (error: any) {
            console.error("Error fetching kit types:", error);
            return rejectWithValue(error.message);
        }
    }
);

const kitsSlice = createSlice({
    name: "kits",
    initialState,
    reducers: {
        clearSelectedKit: (state) => {
            state.selectedKit = null;
        },
        setSelectedKit: (state, action) => {
            state.selectedKit = action.payload;
            state.isKitLoaded = true;
        },
        setCurrentKitPage: (state, action) => {
            state.currentPage = action.payload;
        },
        resetKits: (state) => {
            kitsAdapter.removeAll(state);
            state.totalCount = 0;
            state.hasReachedEnd = false;
            state.isKitLoaded = false;
            state.currentPage = 1;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchKits.pending, (state) => {
                state.status = "loading";
            })
            .addCase(fetchKits.fulfilled, (state, action) => {
                const fetchedPage = action.meta.arg.page || 1;
                if (fetchedPage === 1) {
                    kitsAdapter.setAll(state, action.payload.data as unknown as ICuratedKit[]);
                } else {
                    kitsAdapter.upsertMany(state, action.payload.data as unknown as ICuratedKit[]);
                }
                state.totalCount = action.payload.total;
                const pageSize = action.meta.arg.pageSize || 20;
                state.hasReachedEnd = fetchedPage * pageSize >= state.totalCount;
                state.isKitLoaded = true;
                state.status = "succeeded";
                state.error = null;
            })
            .addCase(fetchKits.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.payload as string;
            })
            .addCase(fetchKitById.pending, (state) => {
                state.status = "loading";
                state.isKitLoaded = false;
                state.error = null;
            })
            .addCase(fetchKitById.fulfilled, (state, action) => {
                state.selectedKit = action.payload;
                // Update the cache (adapter) with the full details
                kitsAdapter.upsertOne(state, action.payload);
                state.isKitLoaded = true;
                state.status = "succeeded";
            })
            .addCase(fetchKitById.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.payload as string;
            })
            .addCase(fetchKitTypes.pending, (state) => {
                state.typesStatus = "loading";
            })
            .addCase(fetchKitTypes.fulfilled, (state, action) => {
                state.types = action.payload;
                state.typesStatus = "succeeded";
                state.isTypesLoaded = true;
            })
            .addCase(fetchKitTypes.rejected, (state, action) => {
                state.typesStatus = "failed";
            });
    },
});

export const {
    clearSelectedKit,
    setSelectedKit,
    setCurrentKitPage,
    resetKits,
} = kitsSlice.actions;

export default kitsSlice.reducer;

const selectKitsState = (state: RootState) => state.kits;

export const {
    selectAll: selectAllKits,
    selectById: selectKitById,
    selectEntities: selectKitEntities,
    selectTotal: selectTotalKits,
} = kitsAdapter.getSelectors(selectKitsState);

export const selectKitStatus = (state: RootState) => state.kits.status;
export const selectKitError = (state: RootState) => state.kits.error;
export const selectKitTotalCount = (state: RootState) => state.kits.totalCount;
export const selectKitCurrentPage = (state: RootState) => state.kits.currentPage;
export const selectKitHasReachedEnd = (state: RootState) => state.kits.hasReachedEnd;
export const selectSelectedKit = (state: RootState) => state.kits.selectedKit;
export const selectIsKitLoaded = (state: RootState) => state.kits.isKitLoaded;
export const selectKitTypes = (state: RootState) => state.kits.types;
export const selectKitTypesStatus = (state: RootState) => state.kits.typesStatus;
export const selectIsKitTypesLoaded = (state: RootState) => state.kits.isTypesLoaded;
